import { Flex } from '@chakra-ui/layout';
import { FormHandles } from '@unform/core';
import {
  Button,
  SelectInput,
  StatusTagBudgetControl,
  TextInput,
  Unform,
  Loading,
  AuthorizeView,
} from 'components';
import { TableServices } from 'components/Table/TableServices';

import { TableServiceBudgetControl } from 'components/TableServiceBudgetControl/index';

import { demandStatusLabels, demandStatusVariant } from 'constants/demands';
import { routePaths } from 'constants/routes';
import { useBoolean } from 'hooks/useBoolean';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import {
  getBudgetControlServices,
  getBudgetControlServiceTotal,
  IGetBudgetControlService,
  IGetBudgetControlServices,
  IGetBudgetControlServiceTotal,
} from 'services/budgetControl';
import { debounce } from 'utils/debounce';
import { formatCurrency } from 'utils/number';
import { objToQuery } from 'utils/query';
import { ServiceModal } from './components';
import * as Styles from './styles';

interface IProps {
  history: RouteComponentProps['history'];
}

interface IPagination {
  totalPages: number;
}

const statusOptions = [
  { label: 'Finalizado', value: 'FINALIZADO' },
  { label: 'Pendente', value: 'PENDENTE' },
  { label: 'Não iniciado', value: 'NAO_INICIADO' },
  { label: 'Em curso', value: 'EM_CURSO' },
  { label: 'Cancelado', value: 'CANCELADO' },
];

const ServicesComponent: React.FC<IProps> = ({ history }) => {
  const [totalData, setTotalData] = useState<IGetBudgetControlServiceTotal>({
    totalServicos: 0,
    valorInvestido: 0,
  });
  const [pagination, setPagination] = useState<IPagination>({ totalPages: 0 });
  const [services, setServices] = useState<IGetBudgetControlServices['content']>([]);
  const [selectedService, setSelectedService] = useState<IGetBudgetControlService>();
  const formRef = useRef<FormHandles>(null);
  const [openServiceModal, setOpenServiceModal] = useBoolean(false);
  const [isLoading, toggleIsLoading] = useBoolean(false);
  const [count, setCount] = useState(0);

  const handleGetTotalData = useCallback(async (query?: string) => {
    const response = await getBudgetControlServiceTotal(query || '');
    setTotalData(response.data);
  }, []);

  const handlePagination = async (page?: number) => {
    const search = history.location.search;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const searchWithoutPages = () => {
      if (search.includes('&page=')) {
        if (isNaN(Number(search.slice(-2)))) {
          return search.slice(0, -7);
        }
      } else {
        return search.slice(0, -8);
      }
    };
    const [url] = search.split('&page=');

    if (history.location.search.includes('&page=0') && history.location.search) {
      setCount(page === undefined ? 0 : page - 1);

      history.replace({
        pathname: routePaths.budgetControl,
        search: `${url}&page=${page === undefined ? 0 : page - 1}`,
      });
    } else if (!history.location.search.includes('&page')) {
      setCount(page === undefined ? 0 : page - 1);

      history.replace({
        pathname: routePaths.budgetControl,
        search: `${url}&page=${page === undefined ? 0 : page - 1}`,
      });
    } else {
      history.replace({
        pathname: routePaths.budgetControl,
        search: `${url}&page=${page === undefined ? 0 : page - 1}`,
      });
      setCount(page === undefined ? 0 : page - 1);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSearch = async (query?: string) => {
    try {
      toggleIsLoading(true);

      const formData = formRef.current?.getData();

      const formQuery = objToQuery({
        ...(formData || {}),
        sort: 'demanda,asc',
      });
      const historyQuery = history.location.search;

      const querySend = historyQuery + `&${formQuery}`;

      const response = await getBudgetControlServices(`${querySend}&size=20` || '');
      setServices(response.data.content);
      setPagination({ totalPages: response.data.totalPages });

      toggleIsLoading(false);
      setCount(0);
    } catch (err) {}
  };

  const handleClickDetails = useCallback((service: IGetBudgetControlService) => {
    setSelectedService(service);
    setOpenServiceModal(true);
  }, []);

  useEffect(() => {
    handleGetTotalData();
    handleSearch();

    handlePagination();

    const removeListen = history.listen(location => {
      formRef.current?.reset();
      handleSearch(location.search);
      handleGetTotalData(location.search);
    });

    return () => {
      removeListen();
    };
  }, []);

  return (
    <Styles.Container>
      <Styles.Header>
        <Unform formRef={formRef}>
          <Styles.HeaderContent>
            <Flex width={'100%'} margin="-10px 0 -10px 0px">
              <legend>Serviços</legend>
            </Flex>
            <Flex width="100%">
              <Flex flex={1} marginRight={'1rem'}>
                <TextInput
                  name="demanda"
                  placeholder="buscar"
                  leftIcon="search"
                  notErrorMargin
                  onChange={debounce(handleSearch, 500)}
                />
              </Flex>

              <Flex flex={1}>
                <SelectInput
                  name="status"
                  placeholder="Status"
                  options={statusOptions}
                  notErrorMargin
                  onChange={debounce(handleSearch, 100)}
                />
              </Flex>
              <Flex flex={1} />
              <Styles.ContainerTotalServices>
                Total de Serviços: {totalData.totalServicos}
              </Styles.ContainerTotalServices>
            </Flex>
          </Styles.HeaderContent>
        </Unform>
      </Styles.Header>
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <AuthorizeView onlyAdmin>
            <TableServices
              headers={['Demanda', 'Cliente', 'Valor do Serviço', 'Status']}
              sizes={[3, 2, 2, 2]}
              row={[0]}
              rows={services.map(service => [
                service.demanda,
                service.client.name,
                formatCurrency(service.valor),

                <Flex key="button" align={'center'} marginLeft={'2rem'} justify={'space-between'}>
                  <StatusTagBudgetControl
                    key="status"
                    variant={demandStatusVariant[service.status || 'accept']}
                  >
                    {demandStatusLabels[service.status]}
                  </StatusTagBudgetControl>

                  <Button
                    variant="primary"
                    outline
                    dense
                    onClick={() => handleClickDetails(service)}
                    style={{ marginLeft: '1rem' }}
                  >
                    Ver detalhes
                  </Button>
                </Flex>,
              ])}
              pagination={{
                initialPage: count + 1,
                pagesCount: pagination.totalPages,
                onChange: handlePagination,
                disableInitialCallback: true,
              }}
            />
          </AuthorizeView>

          <AuthorizeView onlyClient>
            <TableServiceBudgetControl
              headers={['Demanda', 'Valor do Serviço', 'Status']}
              sizes={[1.5, 2, 2]}
              pagination={{
                initialPage: 1,
                pagesCount: pagination.totalPages,
                onChange: handlePagination,
                disableInitialCallback: true,
              }}
              row={[0]}
              rows={services.map(service => [
                service.demanda,
                formatCurrency(service.valor),
                <Flex
                  key="button"
                  align={'center'}
                  marginLeft={'-2.3rem'}
                  justify={'space-between'}
                  paddingRight="2rem"
                >
                  <StatusTagBudgetControl
                    key="status"
                    variant={demandStatusVariant[service.status || 'accept']}
                  >
                    {demandStatusLabels[service.status]}
                  </StatusTagBudgetControl>

                  <Button
                    variant="primary"
                    outline
                    dense
                    onClick={() => handleClickDetails(service)}
                  >
                    Ver detalhes
                  </Button>
                </Flex>,
              ])}
            />
          </AuthorizeView>

          <ServiceModal
            open={openServiceModal}
            onClose={() => setOpenServiceModal(false)}
            serviceId={selectedService?.id}
          />
        </>
      )}
    </Styles.Container>
  );
};

export const Services = memo(ServicesComponent);
